import React, { useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Table from 'react-bootstrap/Table'
import ApplyPanel from './ApplyPanel'
import Pagination from '../../../component/Pagination'
import useDecisions from './useDecisions'

// Courses that do not exist: somebody's name, a swear word, a scratch entry.
//
// Sorted confident-first, matching what the runner printed. A course that has been
// played is still listed — a played layout means a real person stood on a real tee,
// whatever the course is called — but it is a question for you rather than a
// candidate, so it never arrives pre-decided.
export default function HidesPass({ country, data, onDryRun, onApply, applying, dryRunSeen }) {
  const [page, setPage] = useState(0)
  const { decisions, setDecision, decidedCount, clear } = useDecisions('hides', country)

  const candidates = useMemo(() => data?.candidates || [], [data])

  if (!data) return null

  const submit = (dryRun) => {
    const payload = Object.entries(decisions)
      .filter(([, action]) => action === 'HIDE')
      .map(([courseId, action]) => {
        const row = candidates.find((c) => String(c.courseId) === String(courseId))
        return { courseId: Number(courseId), oldName: row ? row.name : '', action }
      })
    ;(dryRun ? onDryRun : onApply)(payload, dryRun ? undefined : clear)
  }

  return (
    <>
      {/* The number that explains a short list. */}
      <Alert variant="light" className="small">
        {data.scorecardsExcluded} scorecard-style names were excluded before anything
        else ran. People create them to keep score without caring about a specific
        course — they are useful, and they are never hidden here.
      </Alert>

      <Pagination items={candidates} page={page} onPage={setPage}>
        {(rows) => (
          <Table size="sm" responsive hover className="curation-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th>Verdict</th>
                <th>Layouts</th>
                <th>Played</th>
                <th>Coords</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const decision = decisions[row.courseId]
                return (
                  <tr
                    key={row.courseId}
                    className={row.confident ? undefined : 'table-warning'}
                  >
                    <td>{row.courseId}</td>
                    <td>
                      {row.name}
                      {row.secondaryName && (
                        <span className="text-muted"> · {row.secondaryName}</span>
                      )}
                      {row.matchesCreator && (
                        <Badge bg="info" className="ms-2">named after its creator</Badge>
                      )}
                    </td>
                    <td className="small">{row.verdict}</td>
                    <td>{row.layoutCount}</td>
                    <td>
                      {row.played > 0 ? (
                        <strong title="Somebody has actually played here">{row.played}</strong>
                      ) : (
                        0
                      )}
                    </td>
                    <td>{row.hasCoordinates ? 'yes' : 'no'}</td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button
                          variant={!decision ? 'secondary' : 'outline-secondary'}
                          onClick={() => setDecision(row.courseId, undefined)}
                        >
                          Leave
                        </Button>
                        <Button
                          variant={decision === 'HIDE' ? 'danger' : 'outline-danger'}
                          onClick={() => setDecision(row.courseId, 'HIDE')}
                        >
                          Hide
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Pagination>

      <ApplyPanel
        decidedCount={decidedCount}
        total={candidates.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
